import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Lock, User, Shield, Sun, Moon } from 'lucide-react';

function getTheme() {
  return localStorage.getItem('theme') || 'dark';
}

function setThemeOnDoc(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('theme', theme);
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState(getTheme());

  useEffect(() => { setThemeOnDoc(theme); }, [theme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try { await authAPI.changePassword(oldPw, newPw); toast.success('Password changed'); setOldPw(''); setNewPw(''); setConfirmPw(''); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="page-container" style={{ maxWidth: 700 }}>
      <div className="page-header"><div><h1 className="page-title">Settings</h1><p className="page-subtitle">Manage your account and preferences</p></div></div>

      {/* Theme Toggle */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-body">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-heading)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
            {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />} Appearance
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: '.95rem', color: 'var(--text-primary)' }}>Theme: <strong style={{ textTransform: 'capitalize' }}>{theme}</strong></p>
              <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 4 }}>Switch between dark and light mode</p>
            </div>
            <button onClick={toggleTheme} className="btn btn-secondary" style={{ minWidth: 140 }}>
              {theme === 'dark' ? <><Sun size={16} /> Light Mode</> : <><Moon size={16} /> Dark Mode</>}
            </button>
          </div>
        </div>
      </div>

      {/* Profile */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-body">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-heading)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}><User size={20} /> Profile</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 32px' }}>
            <div><div className="form-label">User ID</div><p style={{ fontSize: '.95rem' }}>{user?.username}</p></div>
            <div><div className="form-label">Role</div><p style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{user?.is_admin && <Shield size={14} style={{ color: 'var(--accent)' }} />}<span style={{ fontSize: '.95rem' }}>{user?.role} (Type {user?.usertype})</span></p></div>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="card">
        <div className="card-body">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-heading)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}><Lock size={20} /> Change Password</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group"><label className="form-label">Current Password</label><input className="form-input" type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} required /></div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">New Password</label><input className="form-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required /></div>
              <div className="form-group"><label className="form-label">Confirm</label><input className="form-input" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required /></div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}><Lock size={16} /> {saving ? 'Changing...' : 'Change Password'}</button>
          </form>
        </div>
      </div>
    </div>
  );
}